import { useContext, useRef, useState } from 'react';
import { appContext, paths } from '@utils';
import { useFormik } from 'formik';
import { UserType } from '@graphql';
import Box from '@mui/material/Box';
import { Cover, CustomButton, ErrorComponent, Input, InvisibleReCaptcha, InvisibleReCaptchaHandle } from '@components';
import { Link } from 'react-router-dom';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import Avatar from '@mui/material/Avatar';
import styled from '@mui/system/styled';
import Typography from '@mui/material/Typography';
import * as Yup from 'yup';
import recoveryEmailImage from '@assets/img/recovery-email-logo.png';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { store } from '@store';
import { COLORS } from '@styles';

const StyledAvatar = styled(Avatar)`
  background-color: #eef0f6;
`;

const makeStyles = () => ({
  pageContainer: {
    height: 'inherit',
    position: 'relative',
    backgroundColor: 'white',
    display: 'flex',
    overflow: 'hidden',
  },
  formContainer: {
    backgroundColor: 'white',
    width: '50%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '2.5rem',
  },
  formContent: {
    margin: '3.5rem auto',
    width: '24.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  backToLogin: {
    width: '100%',
    margin: '0 1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    '& a': {
      textDecoration: 'none',
      display: 'inherit',
      alignItems: 'inherit',
      fontFamily: 'Inter',
      fontSize: '0.875rem',
      gap: 'inherit',
      fontWeight: 600,
      lineHeight: '1rem',
      color: '#232A43',
    },
  },

  formTitle: {
    fontFamily: 'Urbanist',
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: '2.5rem',
    color: '#232A43',
  },

  formInfo: {
    fontFamily: 'Inter',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: '1rem',
    color: '#445488',
  },

  recoveryEmailSuccessContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '50%',
  },
  recoveryEmailSuccessContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    maxWidth: '390px',
  },
  recoveryEmailImage: {
    width: 'auto',
    height: '187px',
  },
  recoveryEmailMessage: {
    textAlign: 'center',
    color: COLORS.NEUTRAL_100,
    fontSize: '16px',
  },
  recoveryEmailSuccessReminder: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    color: COLORS.NEUTRAL_DARK_0,
    fontSize: '14px',
    padding: '1rem',
    border: `1px solid ${COLORS.NEUTRAL_LIGHT_2}`,
    borderRadius: '0.5rem',

    '& svg': {
      fontSize: '1.5rem',
      color: COLORS.NEUTRAL_LIGHT_0,
    },
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '50%',
  },
});

enum ForgotPasswordSuccessPageStatus {
  DEFAULT = 'default',
  SUCCESS = 'success',
  ERROR = 'error',
}

export function ForgotPasswordSuccessPage() {
  const sx = makeStyles();

  const navigateBackToLogin = () => {
    store.historyStore.history.push(paths.login);
  }

  return (
    <Box sx={sx.recoveryEmailSuccessContainer}>
      <Box sx={sx.recoveryEmailSuccessContent}>
        <img src={recoveryEmailImage} alt="recovery email" style={sx.recoveryEmailImage} />
        <Typography variant="h4" sx={sx.formTitle}>
          Check your email
        </Typography>
        <Typography variant="subtitle1" sx={sx.recoveryEmailMessage}>
          If there is an account associated with this email, you will receive a recovery password link on
          your inbox.
        </Typography>
        <Box sx={sx.recoveryEmailSuccessReminder}>
          <ErrorOutlineOutlinedIcon />
          <Typography variant="subtitle1" sx={sx.formInfo}>
            If you don&apos;t receive it in your inbox, please check your spam or junk folder as well.
          </Typography>
        </Box>
        <CustomButton variant="default" type="submit" onClick={navigateBackToLogin}>
          Back to Sign In
        </CustomButton>
      </Box>
    </Box>
  );
}

export function ForgotPasswordErrorPage({ handleRetry, handleReturn }: { handleRetry: () => void, handleReturn: () => void }) {
  const sx = makeStyles();

  return (
    <Box sx={sx.errorContainer}>
      <ErrorComponent onPrimaryButtonClick={handleRetry} onSecondaryButtonClick={handleReturn} />
    </Box>
  );
}

export function ForgotPasswordPage() {
  const sx = makeStyles();
  const {
    userStore: { resetPasswordRequest },
  } = useContext(appContext);
  const recaptchaRef = useRef<InvisibleReCaptchaHandle>(null);
  const isCaptchaEnabled = !!process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const [status, setStatus] = useState<ForgotPasswordSuccessPageStatus>(ForgotPasswordSuccessPageStatus.DEFAULT);

  const formik = useFormik({
    initialValues: { email: '', type: UserType.SuperAdmin },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Required'),
    }),
    onSubmit: async (values) => {
      try {
        setStatus(ForgotPasswordSuccessPageStatus.DEFAULT);
        const recaptchaToken = await recaptchaRef?.current?.execute();
        if (!recaptchaToken && isCaptchaEnabled) return;
        await resetPasswordRequest({ ...values, recaptchaToken: recaptchaToken || '' });
        setStatus(ForgotPasswordSuccessPageStatus.SUCCESS);
      } catch {
        setStatus(ForgotPasswordSuccessPageStatus.ERROR);
      }
    },
  });

  return (
    <Box sx={sx.pageContainer}>
      {/* Left side */}
      <Cover />
      {/* Right side */}
      {status === ForgotPasswordSuccessPageStatus.DEFAULT && (
      <Box sx={sx.formContainer}>
          <Box sx={sx.backToLogin}>
            <Link to={paths.login}>
              <ArrowBackIosOutlinedIcon />
              Back to login
            </Link>
          </Box>
          <Box component="form" onSubmit={formik.handleSubmit} sx={sx.formContent}>
            <Box display="inline-flex" gap="1rem" alignItems="center" sx={sx.formTitle}>
              <StyledAvatar>🔑</StyledAvatar>
              Reset your password
            </Box>
            <Typography variant="subtitle1" sx={sx.formInfo}>
              Enter your registered email address associated with your account. We&apos;ll send you an
              email with instructions to reset your password.
            </Typography>
            <Box>
              <Input
                value={formik.values.email}
                inputId="email-input"
                label="Email"
                name="email"
                onChange={formik.handleChange}
              />
            </Box>
            <CustomButton variant="default" type="submit" isLoading={formik.isSubmitting}>
              Continue
            </CustomButton>
            <InvisibleReCaptcha ref={recaptchaRef} />
          </Box>
        </Box>
      )}
      {status === ForgotPasswordSuccessPageStatus.SUCCESS && (
        <ForgotPasswordSuccessPage />
      )}
      {status === ForgotPasswordSuccessPageStatus.ERROR && (
        <ForgotPasswordErrorPage handleRetry={formik.handleSubmit} handleReturn={() => setStatus(ForgotPasswordSuccessPageStatus.DEFAULT)} />
      )}
    </Box>
  );
}
